import { useSelector } from 'react-redux';
import { useGetTopBotsQuery } from '../../../store/services/api';
import React, { useRef, useState } from 'react';
import { colors } from '../../../colors';
import {
  selectDisplayBots,
  selectIsDisplayBotsLoading,
} from '../../../store/slices/bot';
import LoadingIcons from 'react-loading-icons';
import { selectSubscriptionSummaries } from '../../../store/slices/subscription';
import BotItem from './BotItem';
import DiscoverHeader from '../Header';
import { selectUserId } from '../../../store/slices/user';
import { SortBy } from '../types';

interface Props {
  availableStableFunds: Record<string, Record<string, number>>;
  isLoading: boolean;
  refresh: () => void;
  credits: number | undefined;
  balances: Record<string, Record<string, number>> | undefined;
}

// User has 50 credits
// Each bot they use costs 2 credits
// If an other user also uses that bot, it now costs 1 credit
// So if the user has 10 bots, but two of them are mutual, they have 2 credits over
// And are able to use an other bot
// To calulate the credits, fetch active user susbcriptions and other subs of same bot
// If no other exist, count 2 credits used, if others found, count 1 credit
// Subtract from 50

// When the user wants to request a bot, this costs 2 credits
// If they want to subscribe, that costs 1 credit

const SearchCoin = (props: Props) => {
  const { availableStableFunds, isLoading, refresh, credits, balances } = props;

  const isDisplayBotsLoading = useSelector(selectIsDisplayBotsLoading);
  const subscriptions = useSelector(selectSubscriptionSummaries);
  const botsRef = useRef<HTMLDivElement | null>(null);

  const userId = useSelector(selectUserId);
  const bots = useSelector(selectDisplayBots);
  const [query, setQuery] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState<SortBy>(SortBy.Volume);

  useGetTopBotsQuery(
    { userId, query: query || undefined, sortBy },
    { refetchOnMountOrArgChange: true },
  );

  const [activeBotForm, setActiveBotForm] = useState<string | undefined>();

  const hasBots = !!bots && !!Object.keys(bots)?.length;
  const showIconLoading = isDisplayBotsLoading || isLoading;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        marginTop: 5,
        paddingLeft: 30,
        paddingRight: 30,
        overflowY: activeBotForm ? 'hidden' : 'auto',
      }}
    >
      <DiscoverHeader
        title={'Search Coins'}
        availableStableFunds={availableStableFunds}
        query={query}
        setQuery={setQuery}
        isLoading={showIconLoading}
        credits={credits}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOptions={[
          { value: SortBy.Volume, label: 'Volume' },
          { value: SortBy.StableGrowth, label: 'Stable Growth' },
        ]}
      />

      {!hasBots && isDisplayBotsLoading && (
        <div
          style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LoadingIcons.Grid width={60} height={60} />
        </div>
      )}

      {!hasBots && !isDisplayBotsLoading && (
        <div
          style={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span
            style={{ color: colors.white, fontSize: 24, fontWeight: 'bold' }}
          >
            No Bots found.
          </span>
        </div>
      )}

      {hasBots && (
        <div
          ref={botsRef}
          style={{
            display: 'flex',
            gap: 20,
            width: '100%',
            flexWrap: 'wrap',
            justifyContent: 'space-evenly',
            marginBottom: 16,
            position: 'relative',
          }}
        >
          {Object.entries(bots).map(([baseAsset, options]) => (
            <BotItem
              key={baseAsset}
              userId={userId}
              baseAsset={baseAsset}
              options={options}
              balances={balances}
              activeBotForm={activeBotForm}
              setActiveBotForm={setActiveBotForm}
              credits={credits ?? 0}
              subscriptions={subscriptions}
              refresh={refresh}
              parentRef={botsRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchCoin;
